import { SiteVisibilityReport } from "~/api/Api";
import { Icon } from "~/assets/icons/Icon";
import { pluralize } from "~/helpers/helpers";

import styles from "./VisibilityImprovementBanner.module.scss";

interface Props {
  report: SiteVisibilityReport;
  /**
   * "active" — engine is healthy, summarising the achieved improvement.
   * "projection" — engine not yet active, previewing the expected gain.
   */
  variant?: "active" | "projection";
}

// Above this measured visibility, the "from X% to 100%" framing reads as a
// no-op (e.g. "Boost from 100% to 100%"), so we lead with the engine's other
// wins — performance and accessibility — instead.
const HIGH_VISIBILITY_THRESHOLD = 95;

// The "after" is perfect by definition (bots are served fully prerendered
// content), so we compare the real measured average against 100% — either as an
// achieved improvement (active) or an expected one (projection).
const VisibilityImprovementBanner = ({ report, variant = "active" }: Props) => {
  const avgBefore = Math.min(100, Math.round(report.avg_text_ratio * 100));
  const gain = 100 - avgBefore;
  const pages = report.pages_analyzed;
  const pageLabel = `${pages.toLocaleString()} ${pluralize("page", pages)}`;
  const isProjection = variant === "projection";
  const isHighVisibility = avgBefore > HIGH_VISIBILITY_THRESHOLD;

  let title: string;
  if (isHighVisibility) {
    title = isProjection
      ? "Boost your page performance and accessibility"
      : "Boosting your page performance and accessibility";
  } else if (isProjection) {
    title = `Boost your AI content visibility from ${avgBefore}% to 100%`;
  } else {
    title = `AI content visibility jumped from ${avgBefore}% to 100%`;
  }

  let gainSuffix = ".";
  if (gain > 0) {
    gainSuffix = isProjection
      ? ` — an estimated ${gain}-point lift.`
      : ` — a ${gain}-point improvement.`;
  }

  return (
    <div
      className={`${styles.banner} ${isProjection ? styles.projection : ""}`}
    >
      <span className={styles.icon}>
        <Icon iconName="SiteSpeedIcon" width="2.5rem" height="2.5rem" />
      </span>
      <div className={styles.text}>
        <div className={styles.title}>{title}</div>
        <div className={styles.body}>
          {isProjection
            ? `Install the Visibility Engine to serve all content to ChatGPT, Claude, Gemini and all other LLMs across ${pageLabel}`
            : `The Visibility Engine is serving all content to ChatGPT, Claude, Gemini and all other LLMs across ${pageLabel}`}
          {gainSuffix}
        </div>
      </div>
    </div>
  );
};

export default VisibilityImprovementBanner;
